import { NetworkStatus } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';

import { ErrorIndicator } from '@components/ErrorIndicator';
import { Layout } from '@components/Layout';
import { paths } from '@constants/paths';
import { Cashback as CashbackUi } from '@features/Cashback';
import { cashbackCalculatorRetailersQuery } from '@features/Cashback/Calculator/query';
import { cashbackReviewsQuery } from '@features/Cashback/query';
import { ProductType, Quote, RetailPartner } from '@generated/gql/graphql';
import { useBundleBasket } from '@hooks/useBundleBasket';
import { useDetectMidJourneyLogin } from '@hooks/useDetectMidJourneyLogin';
import { usePageStateHandler } from '@hooks/usePageStateHandler';
import { withProfiler } from '@services/sentry/withProfiler';
import { getIsLoadingQuery } from '@utils/getIsLoadingQuery';

const PAGE_TITLE = 'Cashback Card';

interface GetLoadingStateProps {
  reviewsNetworkStatus: NetworkStatus;
  retailersNetworkStatus: NetworkStatus;
  basketLoading: boolean;
  reviewsData: unknown;
  retailersData: unknown;
}

const getLoadingState = ({
  reviewsNetworkStatus,
  retailersNetworkStatus,
  basketLoading,
  reviewsData,
  retailersData,
}: GetLoadingStateProps) => {
  const isLoadingReviews = getIsLoadingQuery(reviewsNetworkStatus, reviewsData);
  const isLoadingRetailers = getIsLoadingQuery(
    retailersNetworkStatus,
    retailersData
  );

  return isLoadingReviews || isLoadingRetailers || basketLoading;
};

const CashbackUnwrapped = () => {
  const navigate = useNavigate();
  const {
    basket,
    basketRaw,
    error,
    loading: basketLoading,
    basketData,
    coreServiceCount,
  } = useBundleBasket({});

  useDetectMidJourneyLogin({ basketData, loading: basketLoading });
  const { data: reviewsData, networkStatus: reviewsNetworkStatus } =
    useQuery(cashbackReviewsQuery);
  const { data: retailersData, networkStatus: retailersNetworkStatus } =
    useQuery(cashbackCalculatorRetailersQuery);

  const quote = basketRaw?.quotes.find(
    (quote: { product: { type: ProductType } }) =>
      quote.product.type === ProductType.CashbackCard
  );

  const loading = getLoadingState({
    reviewsNetworkStatus,
    retailersNetworkStatus,
    basketLoading,
    reviewsData,
    retailersData,
  });

  const { isLoading, hasError } = usePageStateHandler({
    loading,
    hasData: !!basket || !!quote,
    error,
  });

  if (isLoading) {
    return null;
  }

  return (
    <Layout
      title={PAGE_TITLE}
      onBack={() => navigate(paths.bundle)}
      backButtonLabel="Back to bundle"
    >
      {hasError && <ErrorIndicator />}

      {!hasError && (
        <CashbackUi
          quote={quote as Quote}
          partners={
            (retailersData?.cashbackCalculatorRetailers
              .partners as RetailPartner[]) ?? []
          }
          coreServiceCount={coreServiceCount}
        />
      )}
    </Layout>
  );
};

const Cashback = withProfiler(CashbackUnwrapped);

export { Cashback };
